import React, {
  useState, useMemo, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  SlideContent,
  ModalDescription,
  ButtonsContainer,
  PrimaryButton,
  SecondaryButton,
} from '../shared';

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e0e0e0;
  width: 100%;
`;

const Tab = styled.button`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.active ? '#444444' : '#9e9e9e')};
  background: none;
  border: none;
  border-bottom: 2px solid ${(props) => (props.active ? '#444444' : 'transparent')};
  margin-bottom: -2px;
  padding: 8px 16px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #444444;
  }

  &:focus {
    outline: none;
  }
`;

const SearchBarContainer = styled.div`
  display: flex;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  height: 40px;
  width: 100%;

  &:focus-within {
    border-color: #b0b0b0;
  }
`;

const SearchIconContainer = styled.div`
  height: 100%;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #444444;
  outline: none;

  &::placeholder {
    color: #9e9e9e;
  }
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const SelectAllCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #444444;
  cursor: pointer;
  flex-shrink: 0;
`;

const SelectAllText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.hasSelection ? '700' : '400')};
  color: ${(props) => (props.hasSelection ? '#444444' : '#9e9e9e')};
  cursor: pointer;
  user-select: none;
`;

const CalendarList = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const CalendarItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  color: #444444;
  border-bottom: 1px solid #f0f0f0;
  user-select: none;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9f9f9;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #444444;
  cursor: pointer;
  flex-shrink: 0;
`;

const EmptyMessage = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #9e9e9e;
  padding: 24px 0;
  text-align: center;
`;

const ErrorMessage = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #d32f2f;
  padding: 24px 0;
  text-align: center;
`;

const CalendarSelectorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TABS = { PARENT: 'parent', ATHLETE: 'athlete' };

const BulkCopySlide2 = ({
  accountCalendars,
  athleteCalendars,
  selectedAccountCalendarIds,
  selectedAthleteCalendarIds,
  onToggleAccountCalendar,
  onToggleAthleteCalendar,
  onSetAccountCalendarIds,
  onSetAthleteCalendarIds,
  isLoading,
  error,
  onBack,
  onNext,
}) => {
  const [activeTab, setActiveTab] = useState(TABS.PARENT);
  const [search, setSearch] = useState('');

  const filteredAccountCalendars = useMemo(() => {
    if (!search) return accountCalendars;
    return accountCalendars.filter((cal) => cal.name.toLowerCase().includes(search.toLowerCase()));
  }, [accountCalendars, search]);

  const filteredAthleteCalendars = useMemo(() => {
    if (!search) return athleteCalendars;
    return athleteCalendars.filter((cal) => `${cal.first} ${cal.last}`.toLowerCase().includes(search.toLowerCase()));
  }, [athleteCalendars, search]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch('');
  };

  const isNextDisabled = selectedAccountCalendarIds.length === 0
    && selectedAthleteCalendarIds.length === 0;

  // ─── Select-all logic per tab ─────────────────────────────────────────────

  const currentFilteredItems = activeTab === TABS.PARENT
    ? filteredAccountCalendars
    : filteredAthleteCalendars;

  const currentSelectedIds = activeTab === TABS.PARENT
    ? selectedAccountCalendarIds
    : selectedAthleteCalendarIds;

  const currentSelectedCount = activeTab === TABS.PARENT
    ? selectedAccountCalendarIds.length
    : selectedAthleteCalendarIds.length;

  const allCurrentSelected = currentFilteredItems.length > 0
    && currentFilteredItems.every((item) => currentSelectedIds.includes(item.id));

  const someCurrentSelected = currentFilteredItems.some(
    (item) => currentSelectedIds.includes(item.id),
  ) && !allCurrentSelected;

  const handleSelectAll = () => {
    const filteredIds = currentFilteredItems.map((item) => item.id);
    const setIds = activeTab === TABS.PARENT ? onSetAccountCalendarIds : onSetAthleteCalendarIds;
    if (allCurrentSelected) {
      setIds(currentSelectedIds.filter((id) => !filteredIds.includes(id)));
    } else {
      setIds([...new Set([...currentSelectedIds, ...filteredIds])]);
    }
  };

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someCurrentSelected;
    }
  }, [someCurrentSelected]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderList = () => {
    if (isLoading) {
      return <EmptyMessage>Loading calendars...</EmptyMessage>;
    }
    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>;
    }

    const items = activeTab === TABS.PARENT ? filteredAccountCalendars : filteredAthleteCalendars;

    if (items.length === 0) {
      return (
        <EmptyMessage>
          {search ? 'No calendars match your search.' : 'No calendars available.'}
        </EmptyMessage>
      );
    }

    return items.map((cal) => {
      const isChecked = activeTab === TABS.PARENT
        ? selectedAccountCalendarIds.includes(cal.id)
        : selectedAthleteCalendarIds.includes(cal.id);
      const onToggle = activeTab === TABS.PARENT
        ? () => onToggleAccountCalendar(cal.id)
        : () => onToggleAthleteCalendar(cal.id);

      return (
        <CalendarItem key={cal.id}>
          <Checkbox
            type='checkbox'
            checked={isChecked}
            onChange={onToggle}
          />
          {activeTab === TABS.ATHLETE
            ? `${cal.first} ${cal.last}`
            : cal.name}
        </CalendarItem>
      );
    });
  };

  const calendarWord = activeTab === TABS.ATHLETE ? 'athletes' : 'calendars';

  return (
    <SlideContent>
      <ModalDescription>
        Select the calendar(s) into which you would like to paste the copied
        planner blocks.
      </ModalDescription>

      <CalendarSelectorSection>
        <TabBar>
          <Tab
            active={activeTab === TABS.PARENT}
            onClick={() => handleTabChange(TABS.PARENT)}
            type='button'
          >
            Parent Calendars
          </Tab>
          <Tab
            active={activeTab === TABS.ATHLETE}
            onClick={() => handleTabChange(TABS.ATHLETE)}
            type='button'
          >
            Athlete Calendars
          </Tab>
        </TabBar>

        <SearchBarContainer>
          <SearchIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='magnifying-glass'
              color='#e0e0e0'
            />
          </SearchIconContainer>
          <SearchInput
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search calendars...'
          />
        </SearchBarContainer>

        <SelectAllRow>
          <SelectAllCheckbox
            ref={selectAllRef}
            type='checkbox'
            checked={allCurrentSelected}
            onChange={handleSelectAll}
          />
          <SelectAllText
            hasSelection={currentSelectedCount > 0}
            onClick={handleSelectAll}
          >
            {`Selected (${currentSelectedCount} ${calendarWord})`}
          </SelectAllText>
        </SelectAllRow>

        <CalendarList>
          {renderList()}
        </CalendarList>
      </CalendarSelectorSection>

      <ButtonsContainer>
        <PrimaryButton onClick={onNext} disabled={isNextDisabled}>
          Next
        </PrimaryButton>
        <SecondaryButton onClick={onBack}>
          Back
        </SecondaryButton>
      </ButtonsContainer>
    </SlideContent>
  );
};

BulkCopySlide2.propTypes = {
  accountCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    planId: PropTypes.number,
  })).isRequired,
  athleteCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
    name: PropTypes.string,
    planId: PropTypes.number,
  })).isRequired,
  selectedAccountCalendarIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  selectedAthleteCalendarIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onToggleAccountCalendar: PropTypes.func.isRequired,
  onToggleAthleteCalendar: PropTypes.func.isRequired,
  onSetAccountCalendarIds: PropTypes.func.isRequired,
  onSetAthleteCalendarIds: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

BulkCopySlide2.defaultProps = {
  error: null,
};

export default BulkCopySlide2;
